/**
 * ToolsBox Search Functionality with Auto-complete
 * Reads tools from ToolsConfig - auto-updates when new tools are added
 * Auto-loads ToolsConfig if not present
 */

(function() {
    'use strict';

    let suggestionDropdown = null;
    let selectedIndex = -1;
    let currentSuggestions = [];
    let toolsConfigLoaded = false;

    // Auto-load ToolsConfig if not present (check both global and loaded scripts)
    function ensureToolsConfig() {
        // Check if already loaded via global
        if (window.ToolsConfig && typeof window.ToolsConfig.getAllToolIds === 'function') {
            return Promise.resolve();
        }
        
        // Check if script tag already exists in DOM
        var existingScripts = document.querySelectorAll('script[src*="tools-config"]');
        if (existingScripts.length > 0) {
            // Wait for the script to execute
            return new Promise(function(resolve) {
                var checkCounter = 0;
                var checkInterval = setInterval(function() {
                    if (window.ToolsConfig && typeof window.ToolsConfig.getAllToolIds === 'function') {
                        clearInterval(checkInterval);
                        resolve();
                    } else if (checkCounter++ > 50) { // 5 seconds timeout
                        clearInterval(checkInterval);
                        resolve();
                    }
                }, 100);
            });
        }
        
        // Load dynamically
        return new Promise(function(resolve, reject) {
            var script = document.createElement('script');
            script.src = '/shared/js/tools-config.js';
            script.onload = function() {
                resolve();
            };
            script.onerror = function() {
                console.warn('Failed to load ToolsConfig, using fallback');
                resolve();
            };
            document.head.appendChild(script);
        });
    }

    function getCurrentLang() {
        const htmlLang = document.documentElement.lang || 'en';
        return htmlLang;
    }

    // Get tools from ToolsConfig (auto-generated)
    function getAllTools() {
        if (window.ToolsConfig && typeof window.ToolsConfig.getAllToolIds === 'function') {
            return window.ToolsConfig.getAllToolIds();
        }
        // Fallback: return known tools
        return [
            'img-to-base64', 'base64-to-img', 'json-formatter', 'uuid-generator',
            'weather', '2fa-authenticator', 'password-generator', 'hash-generator',
            'image-compressor', 'timestamp-converter', 'date-difference', 'xml-formatter'
        ];
    }

    function getToolInfo(toolId) {
        const lang = getCurrentLang();
        
        // Try ToolsConfig first
        if (window.ToolsConfig && typeof window.ToolsConfig.getToolInfo === 'function') {
            const info = window.ToolsConfig.getToolInfo(toolId, lang);
            if (info) return info;
        }
        
        // Fallback: return ID as name
        return { id: toolId, name: toolId };
    }

    function getToolUrl(toolId) {
        const lang = getCurrentLang();
        return `/${lang}/${toolId}/`;
    }

    // Search tools based on input
    function searchTools(query) {
        if (!query || query.length < 1) return [];
        
        const searchLower = query.toLowerCase();
        const toolIds = getAllTools();
        const results = [];
        
        for (const toolId of toolIds) {
            const info = getToolInfo(toolId);
            const name = info.name || toolId;
            const nameLower = name.toLowerCase();
            
            // Match at start or anywhere in the name
            if (nameLower.startsWith(searchLower) || nameLower.includes(searchLower)) {
                results.push({
                    id: toolId,
                    name: name,
                    url: getToolUrl(toolId)
                });
            }
        }
        
        return results.slice(0, 6); // Max 6 suggestions
    }

    // Create suggestion dropdown
    function createDropdown() {
        if (suggestionDropdown) return suggestionDropdown;
        
        suggestionDropdown = document.createElement('div');
        suggestionDropdown.id = 'search-suggestions';
        suggestionDropdown.className = 'absolute top-full left-0 right-0 mt-2 bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 overflow-hidden z-50 hidden';
        suggestionDropdown.style.maxHeight = '300px';
        suggestionDropdown.style.overflowY = 'auto';
        
        return suggestionDropdown;
    }

    // Render suggestions in dropdown
    function renderSuggestions(suggestions) {
        if (!suggestionDropdown) return;
        
        currentSuggestions = suggestions;
        selectedIndex = -1;
        
        if (suggestions.length === 0) {
            suggestionDropdown.classList.add('hidden');
            return;
        }
        
        let html = '';
        
        suggestions.forEach(function(tool, index) {
            const iconBg = 'bg-blue-100 dark:bg-blue-900/30';
            const iconText = 'text-blue-600 dark:text-blue-400';
            
            html += `
                <a href="${tool.url}" class="suggestion-item flex items-center gap-3 px-4 py-3 hover:bg-slate-100 dark:hover:bg-slate-700 cursor-pointer transition-colors" data-index="${index}" data-id="${tool.id}">
                    <span class="flex items-center justify-center w-8 h-8 rounded-lg ${iconBg} ${iconText} text-sm font-medium">
                        ${index + 1}
                    </span>
                    <span class="text-slate-700 dark:text-slate-300 text-sm font-medium">${tool.name}</span>
                </a>
            `;
        });
        
        suggestionDropdown.innerHTML = html;
        suggestionDropdown.classList.remove('hidden');
    }

    // Update keyboard selection
    function updateSelection() {
        const items = suggestionDropdown.querySelectorAll('.suggestion-item');
        items.forEach(function(item, index) {
            if (index === selectedIndex) {
                item.classList.add('bg-blue-50', 'dark:bg-blue-900/30');
            } else {
                item.classList.remove('bg-blue-50', 'dark:bg-blue-900/30');
            }
        });
    }

    // Navigate to selected tool
    function navigateToSelected() {
        if (selectedIndex >= 0 && selectedIndex < currentSuggestions.length) {
            const tool = currentSuggestions[selectedIndex];
            window.location.href = tool.url;
        }
    }

    // Show/hide dropdown
    function showDropdown() {
        if (suggestionDropdown && currentSuggestions.length > 0) {
            suggestionDropdown.classList.remove('hidden');
        }
    }

    function hideDropdown() {
        if (suggestionDropdown) {
            suggestionDropdown.classList.add('hidden');
            selectedIndex = -1;
            currentSuggestions = [];
        }
    }

    // Handle keyboard navigation
    function handleKeydown(event) {
        if (!suggestionDropdown || suggestionDropdown.classList.contains('hidden')) return;
        
        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                selectedIndex = Math.min(selectedIndex + 1, currentSuggestions.length - 1);
                updateSelection();
                scrollToSelected();
                break;
                
            case 'ArrowUp':
                event.preventDefault();
                selectedIndex = Math.max(selectedIndex - 1, -1);
                updateSelection();
                scrollToSelected();
                break;
                
            case 'Enter':
                event.preventDefault();
                if (selectedIndex >= 0) {
                    navigateToSelected();
                }
                break;
                
            case 'Escape':
                hideDropdown();
                break;
        }
    }

    // Scroll selected item into view
    function scrollToSelected() {
        const items = suggestionDropdown.querySelectorAll('.suggestion-item');
        if (items[selectedIndex]) {
            items[selectedIndex].scrollIntoView({ block: 'nearest' });
        }
    }

    // Handle search input
    function handleSearch(event) {
        const searchTerm = event.target.value.trim();
        const toolsGrid = document.getElementById('tools-grid');
        
        // Homepage: filter tool cards
        if (toolsGrid) {
            const toolCards = toolsGrid.querySelectorAll('.tool-card');
            const searchLower = searchTerm.toLowerCase();
            
            toolCards.forEach(function(card) {
                const title = card.querySelector('h3');
                const description = card.querySelector('p');
                const text = (title ? title.textContent : '') + ' ' + (description ? description.textContent : '');
                
                if (searchTerm === '' || text.toLowerCase().includes(searchLower)) {
                    card.style.display = '';
                    card.classList.remove('hidden');
                } else {
                    card.style.display = 'none';
                }
            });
            return;
        }
        
        // Tool pages: show suggestions
        if (!suggestionDropdown) return;
        
        const searchInput = event.target;
        const results = searchTools(searchTerm);
        
        if (results.length > 0) {
            renderSuggestions(results);
            showDropdown();
        } else {
            hideDropdown();
        }
    }

    // Handle Enter key on tool pages
    function handleKeypress(event) {
        if (event.key === 'Enter') {
            const searchTerm = event.target.value.trim();
            
            // On tool pages, search suggestions first
            if (!document.getElementById('tools-grid') && searchTerm) {
                const results = searchTools(searchTerm);
                if (results.length > 0) {
                    window.location.href = results[0].url;
                    return;
                }
            }
            
            // On homepage, find matching tool
            const toolIds = getAllTools();
            for (const toolId of toolIds) {
                const info = getToolInfo(toolId);
                const name = (info.name || toolId).toLowerCase();
                if (name.includes(searchTerm.toLowerCase())) {
                    window.location.href = getToolUrl(toolId);
                    return;
                }
            }
        }
    }

    // Handle click outside
    function handleClickOutside(event) {
        const searchInput = document.getElementById('global-search');
        if (suggestionDropdown && searchInput && !searchInput.contains(event.target) && !suggestionDropdown.contains(event.target)) {
            hideDropdown();
        }
    }

    function init() {
        const searchInput = document.getElementById('global-search');
        if (!searchInput) return;
        
        // Ensure ToolsConfig is loaded first
        ensureToolsConfig().then(function() {
            setupSearchInput(searchInput);
        });
    }
    
    function setupSearchInput(searchInput) {
        // Add dropdown after search input's parent
        suggestionDropdown = createDropdown();
        const parent = searchInput.parentElement;
        if (parent) {
            parent.style.position = 'relative';
            parent.appendChild(suggestionDropdown);
        }
        
        // Event listeners
        searchInput.addEventListener('input', handleSearch);
        searchInput.addEventListener('keydown', handleKeydown);
        searchInput.addEventListener('keypress', handleKeypress);
        searchInput.addEventListener('focus', function() {
            const term = searchInput.value.trim();
            if (term) {
                const results = searchTools(term);
                if (results.length > 0) {
                    renderSuggestions(results);
                    showDropdown();
                }
            }
        });
        
        // Close dropdown on click outside
        document.addEventListener('click', handleClickOutside);
        
        // Close dropdown on escape
        document.addEventListener('keydown', function(e) {
            if (e.key === 'Escape') {
                hideDropdown();
                searchInput.blur();
            }
        });
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }

})();
